import {ValidationMessage} from 'common';
import {FormField, majorScale, Text, TextInput, TextInputProps} from 'evergreen-ui';
import React, {memo} from 'react';
import {RegisterOptions, useFormContext} from 'react-hook-form';
import {Observable} from 'rxjs';
import {useObservable} from 'rxsupport';
import {OfferFormInput} from './OfferForm';

type OfferHourFields =
    'directCareHours'
    | 'midLevelSupervisionHours'
    | 'highLevelSupervisionHours'
    | 'groupCareHours'
    | 'caregiverMediatedMidLevelHours'
    | 'caregiverMediatedHighLevelHours'
    | 'draftStaffingPlan.offeredParaprofessionalHours'
    | 'draftStaffingPlan.offeredMidLevelHours'
    | 'draftStaffingPlan.offeredHighLevelHours'
    | 'draftStaffingPlan.offeredGroupCareHours'
    | 'psychotherapyOffer';
type HoursInputProps = {
    name: OfferHourFields;
    label: React.ReactNode;
    isRequired?: boolean;
    isSubmitting$: Observable<boolean>;
    twoDecimalSupport?: boolean,
    recommendedHoursLabel?: (maxHours: number) => React.ReactNode;
    maxValue: number,
    minValue?: number,
} & TextInputProps;
export const HoursInput = memo(({
                                    maxValue,
                                    minValue = NaN,
                                    name,
                                    label,
                                    isSubmitting$,
                                    twoDecimalSupport = false,
                                    recommendedHoursLabel = defaultRecommendedHourLabel,
                                    isRequired = true,
                                    ...rest
                                }: HoursInputProps) => {
    const {
        register,

        formState: {
            errors,
        },
    } = useFormContext<OfferFormInput>();

    const maxHours = (!isNaN(maxValue) && isFinite(maxValue)) ? maxValue : undefined;
    const minHours = (!isNaN(minValue) && isFinite(minValue)) ? minValue : undefined;

    const validationRules: RegisterOptions = {
        required: {
            message: 'This field is required.',
            value: isRequired,
        },
        maxLength: {
            message: twoDecimalSupport ? 'Please enter at most 4 digits. (include 2 decimals)' : 'Please enter at most 2 digits.',
            value: twoDecimalSupport ? 5 : 2
        },
        min:  minHours !== undefined ? {
            message: `Minimum value is ${minHours}`,
            value: minHours,
        } : {
            message: 'Negative number is not allowed.',
            value: 0,
        },
        max: maxHours !== undefined ? {
            message: `Maximum value is ${maxHours}`,
            value: maxHours,
        } : undefined,
        pattern: twoDecimalSupport ? {
            value: /^\d*(\.\d{1,2})?$/,
            message: "Only support up to 2 decimals"
        } : undefined
    };

    const sending = useObservable(isSubmitting$, false);
    const error = name.startsWith('draftStaffingPlan.')
        ? errors.draftStaffingPlan?.[name.split('.').pop() as keyof typeof errors.draftStaffingPlan]
        // @ts-ignore
        : errors[name];

    return (
        <FormField
            marginBottom={24}
            label={label}
            labelFor={name}
            // @ts-ignore
            validationMessage={<ValidationMessage error={error}/>}
        >
            <TextInput
                id={name}
                {...register(name, validationRules)}
                height={majorScale(4)}
                // @ts-ignore
                isInvalid={!!error?.message}
                {...rest}
                type='number'
                min={0}
                max={maxHours}
                disabled={sending}/>
            {defaultRecommendedHourLabel(maxHours, minHours)}
        </FormField>
    );
});

const defaultRecommendedHourLabel = (maxHours?: number, minHours?: number) => {
    if (maxHours === undefined) {
        return null;
    }

    const hoursText = minHours === undefined || minHours === maxHours
        ? `${maxHours} hour${maxHours > 1 ? 's' : ''} recommended`
        : `${minHours} - ${maxHours} hour${maxHours > 1 ? 's' : ''} recommended`;

    return <Text marginLeft={8}>{`(${hoursText})`}</Text>;
};
